/**
 * The Profile screen — `/profile`, bata-ui#132 round 2b, ADR 9012.
 *
 * The product owner's decision of 2026-09-26: a place where the signed-in person keeps the data
 * the product needs from them, including what alert e-mail will need once it is switched on.
 *
 * **Two owners, said on the screen.** Personal data — name, e-mail, whether the e-mail is
 * verified — live in Keycloak, which the reset-password e-mail reads, so they are shown as the
 * token carries them and edited on Keycloak's own forms — the name through `UPDATE_PROFILE`
 * (`editProfile()`), the e-mail through `UPDATE_EMAIL` (`changeEmail()`), which changes the address
 * only once the link mailed to the new one is opened (contract C5). The screen keeps showing the
 * address the token carries, never one typed and not yet confirmed. Nothing personal is typed into
 * this application. Notification
 * preferences live on the platform, per principal (contract C4).
 *
 * **A shell route, like the Cockpit.** A profile belongs to no chapter, so it is mounted by the
 * router's own branch rather than by a manifest (which would need a slug and an ADR block it has
 * no claim to), and reached from the identity menu in both rails and from the command palette.
 */

import { useTranslation } from 'react-i18next';
import { AtSign, KeyRound, UserRoundPen } from 'lucide-react';

import { changeEmail, changePassword, editProfile, useIdentityClaims, type IdentityClaims } from '../../../auth/session';
import { Button, EmptyState, StatusIndicator } from '../../../primitives';
import { Panel } from '../../../planning';

import { ProfileRecord } from './ProfileRecord';
import { codeWords, ROLE_LABEL_KEY } from '@/planning/words';

/** The profile's address. Held as a constant for the router, the rails and the palette. */
export const PROFILE_PATH = '/profile';

function Field({ labelKey, children }: { readonly labelKey: string; readonly children: React.ReactNode }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="border-hairline grid gap-1 border-b py-2 sm:grid-cols-[12rem_1fr] sm:gap-4">
      <dt className="text-label text-ink-secondary">{t(labelKey)}</dt>
      <dd className="text-body text-ink-primary min-w-0">{children}</dd>
    </div>
  );
}

function Absent({ labelKey }: { readonly labelKey: string }): React.JSX.Element {
  const { t } = useTranslation();
  return <span className="text-ink-secondary">{t(labelKey)}</span>;
}

/** Whether the e-mail can carry anything yet, in words and never by colour alone (§12.6). */
function EmailState({ identity }: { readonly identity: IdentityClaims }): React.JSX.Element {
  if (identity.email === null) {
    return <StatusIndicator level="warning" labelKey="profile.email.missing" />;
  }
  if (identity.emailVerified === true) {
    return <StatusIndicator level="good" labelKey="profile.email.verified" />;
  }
  if (identity.emailVerified === false) {
    return <StatusIndicator level="warning" labelKey="profile.email.unverified" />;
  }
  return <Absent labelKey="profile.email.verificationUnknown" />;
}

export function ProfileScreen(): React.JSX.Element {
  const { t } = useTranslation();
  const identity = useIdentityClaims();

  if (identity === null) {
    return <EmptyState titleKey="profile.signedOut.title" bodyKey="profile.signedOut.body" />;
  }

  return (
    <>
      <Panel titleKey="profile.personal.title" subtitleKey="profile.personal.source" aboutKey="profile.personal.about">
        <dl className="flex flex-col">
          <Field labelKey="profile.field.name">
            {identity.name === null ? <Absent labelKey="profile.notSet" /> : <span translate="no">{identity.name}</span>}
          </Field>
          <Field labelKey="profile.field.username">
            {identity.username === null ? <Absent labelKey="profile.notSet" /> : <span translate="no">{identity.username}</span>}
          </Field>
          <Field labelKey="profile.field.email">
            <div className="flex flex-wrap items-center gap-3">
              {identity.email !== null && <span translate="no" className="break-all">{identity.email}</span>}
              <EmailState identity={identity} />
            </div>
          </Field>
          <Field labelKey="profile.field.roles">
            {identity.roles.length === 0 ? <Absent labelKey="profile.roles.none" /> : (
              <ul className="flex flex-wrap gap-2">
                {/* The role in words (audit A9); its realm name in the tooltip. A Keycloak built-in
                    this build has no words for is shown as its name. */}
                {identity.roles.map((role) => (
                  <li key={role} title={role} className="border-hairline text-label rounded-chip border px-2">{codeWords(t, ROLE_LABEL_KEY, role)}</li>
                ))}
              </ul>
            )}
          </Field>
        </dl>
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="secondary" icon={<UserRoundPen size={14} aria-hidden />} onClick={editProfile}>
              {t('profile.personal.editName')}
            </Button>
            <p className="text-caption text-ink-secondary max-w-prose">{t('profile.personal.editNameHint')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="secondary" icon={<AtSign size={14} aria-hidden />} onClick={changeEmail}>
              {t(identity.email === null ? 'profile.personal.addEmail' : 'profile.personal.changeEmail')}
            </Button>
            <p className="text-caption text-ink-secondary max-w-prose">{t('profile.personal.changeEmailHint')}</p>
          </div>
        </div>
      </Panel>

      <Panel titleKey="profile.password.title">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="secondary" icon={<KeyRound size={14} aria-hidden />} onClick={changePassword}>
            {t('nav.changePassword')}
          </Button>
          <p className="text-caption text-ink-secondary max-w-prose">{t('profile.password.hint')}</p>
        </div>
      </Panel>

      {/* Contract C4 — the platform's record: the alert preference and what the profile lacks. */}
      <ProfileRecord identity={identity} />
    </>
  );
}
